/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ServiceContext } from "@msx/platform-services";
import {
  getConsumptionAPI,
  postConsumptionAPI,
} from "../../../../../utils/httpUtils";
import { useMyContext } from "../../../../../context/myContext";
import {
  SETChileComponentName,
  SETHasServiceRequestTypeLogData,
  SETIsCustomeLog,
  SETLogEventData,
  SETServiceRequestTypeLogData,
  getIsUserDataUpdated,
  setRecommendedTiles,
} from "../../../../../../core/store";
import {
  PURL_ERROR_INVALID_PARAMS,
  V2_SERVICE_NOT_IN_PREFERENCE_Americas,
  V2_SERVICE_NOT_IN_PREFERENCE_NonAmericas,
  SERVICE_LOCKED,
  Clicked_On_Pin,
  Recommended_Tile_Pin_Telematry_message,
} from "../../../../../utils/constants";
import {
  checkVisibilityOfTile,
} from "../../../../TileConfiguration/V2_APIHelperFile";
import useLogScreenMetrics from "../../../../../../errorHandling/logScreenMetrics";

// Module-level URL helper — referenced by fetchData (exact copy from V2_Tile.tsx module scope)
const urlParamGet = (params: URLSearchParams, name: string): string | null => {
  const lower = name.toLowerCase();
  for (const [key, value] of Array.from(params.entries())) {
    if (key.toLowerCase() === lower) return value;
  }
  return null;
};

// Module-level Set for O(1) PURL key validation (exact copy from V2_Tile.tsx module scope)
const KNOWN_PURL_KEYS = new Set(['tilename', 'irisutterance', 'area', 'mode', 'segment', 'subsegment', 'tileinfo', 'sendservicegroup', 'defaultservicegroup', 'downloadfile', 'typeoffeedback', 'prompt', 'appname']);

/**
 * useWorkspaceTiles — Manages workspace tile data fetching, caching, and deletion.
 *
 * Extracted from V2_Tile.tsx (PR #5, Phase 1).
 * Zero logic changes — pure cut-paste extraction.
 *
 * useState moved: dataServiceWorkspace, dialogTitle, isDeleteModalVisible, isDropdownModalVisible,
 *   showTileNotFoundModal, isErrorModalVisible, deleteModalId, deleteId, selectedOptions,
 *   irisAppName, value, isLoading, isLoadingRecommended, resultError, errorData, webFormIrisContext,
 *   recommendedTile, ResultErrorRecommendation, isWebFormPanelOpen, selectedTileId, selectedServiceTile,
 *   isTileFoundInPreference, tileAlreadyInWorkspace, serviceDeleted, webFormNames, isGetTileExecuted,
 *   isWorkspaceLoad
 * Functions moved: fetchUserWorkspacesAndDefaultTiles, validateResponse, processRedirectServices,
 *   getTiles, getUnsecuredRecommendedTiles, getRecommendedTiles, fetchTilesData,
 *   checkUrlParams, handleNonAmericaOrAmerica, handleLoadedOrNonAmerica, handleUserDataChange,
 *   fetchData
 * Effects moved: logScreenMetrics, recommendation reload, tile reload on userWelcomeUpdated,
 *   isUserDataChanged localStorage cleanup, main fetchData effect
 */

interface UseWorkspaceTilesParams {
  userId: number;
  addServiceId: any;
  setAddServiceId: (id: any) => void;
  userDataChanged: any;
  currentUserData: any;
  reccomendedData: any;
  recommendationLoaded: any;
  setRecommendationLoaded: (val: boolean) => void;
  userWelcomeUpdated: any;
  finalTileObject: any;
  setProfileLoading: (val: boolean) => void;
  setShowBot: (val: boolean) => void;
  setShowErrorModal: (val: any) => void;
  isCanceledPreset: any;
  autoPinInProgress: React.MutableRefObject<boolean>;
  checkTileSecurity: (tileName: string) => Promise<boolean>;
  purlProcessed: React.MutableRefObject<boolean>;
  isUserLoggedIn: any;
  setActiveTabKey?: (val: string) => void;
  copilotMapFetched: React.MutableRefObject<boolean>;
  setCopilotAccessMap: (map: Record<string, { hasAccess: boolean; enableTileForm: boolean; enableSelfHelp: boolean }>) => void;
}

export const useWorkspaceTiles = (params: UseWorkspaceTilesParams) => {
  const context = React.useContext(ServiceContext);
  const { data, updateContextData } = useMyContext();
  const reduxDispatch = useDispatch();
  const userId = params.userId;

  const [dataServiceWorkspace, setDataServiceWorkspace] = React.useState([]);
  const [dialogTitle, setDialogTitle] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDropdownModalVisible, setIsDropdownModalVisible] = useState(false);
  const [showTileNotFoundModal, setShowTileNotFoundModal] = useState({
    isVisible: false,
    message: "",
    showCatalogButton: false,
  });
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [deleteModalId, setDeleteModalId] = useState(null);
  const [deleteId, setDeleteId] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const irisAppName = useState("")[0];
  const [value, setValue] = React.useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRecommended, setIsLoadingRecommended] = useState(true);
  const [resultError, setResultError] = useState("");
  const [errorData, setErrorData] = useState([]);
  const [webFormIrisContext, setWebFormIrisContext] = useState({
    name: "",
    iriS_Utterance: "",
    tileName: "",
    serviceWithFormKey: "",
  });
  const [recommendedTile, setRecommendedTile] = useState([]);
  const [ResultErrorRecommendation, setResultErrorRecommendation] =
    useState("");
  const [isWebFormPanelOpen, setIsWebFormPanelOpen] = useState(false);
  const [selectedTileId, setSelectedTileId] = useState(null);
  const [selectedServiceTile, setSelectedServiceTile] = useState({});
  const [isTileFoundInPreference, setIsTileFoundInPreference] = useState(false);
  const [tileAlreadyInWorkspace, setTileAlreadyInWorkspace] = useState(false);
  const [serviceDeleted, setServiceDeleted] = useState(false);
  const [webFormNames, setWebFormNames] = useState([]);
  const [isGetTileExecuted, setGetTileExecuted] = useState(false);
  const [isWorkspaceLoad, setWorkspaceLoad] = useState(false);
  reduxDispatch(SETChileComponentName("My Help Workspace"));
  // BUILD_TIMESTAMP is used as fallback for local builds when pipeline doesn't replace {BUILD_VERSION}
  const buildTimestamp = process.env.BUILD_TIMESTAMP || "local";
  // Pipeline replaces {BUILD_VERSION} with Build.BuildNumber (e.g., MCAPSHelp_UI_20260305.1)
  // For local builds, if {BUILD_VERSION} placeholder still exists, fallback to timestamp
  const envKey = process.env.REACT_APP_WEBLOCALSTORAGE_KEY || "v2_webFormNames_1.0.0";
  const WEBFORMNAMES_LOCAL_STORAGE_KEY = envKey.includes("{BUILD_VERSION}") 
    ? envKey.replace("{BUILD_VERSION}", buildTimestamp)
    : envKey;

  const logScreenMetrics = useLogScreenMetrics();
  useEffect(() => {
    logScreenMetrics("My Help Workspace");
  }, [logScreenMetrics]);

  const isUserDataChanged = useSelector(getIsUserDataUpdated);

  useEffect(() => {
    dataServiceWorkspace.length === 0 && getTiles();
  }, [params.userWelcomeUpdated]);

  if (isUserDataChanged) {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(`TileVisibility_${userId}`)) {
        localStorage.removeItem(key);
      }
    }
  }

  const checkResponse = (response) => {
    switch (response.status) {
      case 200:
      case 201:
      case 204:
        return "success";
      case 401:
      case 500:
      default:
        return "error";
    }
  };

  const checkUrlParams = () => {
    const paramurl = window.location.search.toLocaleLowerCase();
    const isparamurlAmerica =
      paramurl.includes("tilename") && paramurl.includes("area");
    const isAmericaSMC =
      paramurl.includes("tilename") &&
      paramurl.includes("area") &&
      paramurl.includes("segment");
    const isparamurlNonAmerica =
      paramurl.includes("tilename") &&
      paramurl.includes("irisutterance") &&
      !paramurl.includes("mode");
    // G9 fix: Detect incident PURLs with TileName+Mode but no Area/IrisUtterance
    const isparamurlIncident =
      paramurl.includes("tilename") &&
      paramurl.includes("mode") &&
      !paramurl.includes("area") &&
      !paramurl.includes("irisutterance");

    return { isparamurlAmerica, isparamurlNonAmerica, isAmericaSMC, isparamurlIncident };
  };

  const fetchUserWorkspacesAndDefaultTiles = async () => {
    const userUpn = sessionStorage.getItem("LoggedInUser") || '';
    const shouldFetchCopilotMap = !params.copilotMapFetched.current;
    if (shouldFetchCopilotMap) params.copilotMapFetched.current = true;
    const copilotMapPromise = shouldFetchCopilotMap
      ? getConsumptionAPI(`/api/V2_User/GetCopilotAccessMap?upn=${encodeURIComponent(userUpn)}`, context.authClient).catch(() => null)
      : Promise.resolve(null);
    const [response1, response2, copilotMapResponse] = await Promise.all([
      getConsumptionAPI(`/api/V2_UserWorkSpace/GetAllWorkspaces/${userId}`, context.authClient),
      getConsumptionAPI(`/api/V2_UserWorkSpace/GetDefaultTileByUserId/${userId}`, context.authClient),
      copilotMapPromise,
    ]);
    const result1 = checkResponse(response1);
    const result2 = checkResponse(response2);
    // Process copilot access map
    if (shouldFetchCopilotMap && copilotMapResponse) {
      // Resolve the actual data — axios wraps in .data, but getConsumptionAPI error path may return raw
      const rawData = copilotMapResponse?.data ?? copilotMapResponse;
      if (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
        const normalized: Record<string, { hasAccess: boolean; enableTileForm: boolean; enableSelfHelp: boolean }> = {};
        for (const [k, v] of Object.entries(rawData)) {
          // Backward compatible: API may return boolean (old) or object with enableTileForm (new)
          normalized[k.toLowerCase()] = typeof v === 'boolean'
            ? { hasAccess: v, enableTileForm: true, enableSelfHelp: false }
            : { hasAccess: (v as any).hasAccess ?? false, enableTileForm: (v as any).enableTileForm ?? true, enableSelfHelp: (v as any).enableSelfHelp ?? false };
        }
        params.setCopilotAccessMap(normalized);
        sessionStorage.setItem('hasCopilotAccess', Object.values(normalized).some(v => v.hasAccess === true) ? 'true' : 'false');
        
        window.dispatchEvent(new Event('copilotAccessMapReady'));
      } else {
        // Response came back but data shape is unexpected — reset flag to allow retry
        console.warn('[CopilotAccessMap] Unexpected response shape, will retry:', rawData);
        params.copilotMapFetched.current = false;
        // Still fire event so P-URL flow doesn't block indefinitely
        window.dispatchEvent(new Event('copilotAccessMapReady'));
      }
    } else if (shouldFetchCopilotMap && !copilotMapResponse) {
      // API call failed (caught by .catch(() => null)) — fire event so P-URL flow proceeds
      window.dispatchEvent(new Event('copilotAccessMapReady'));
    }
    return { response1, response2, result1, result2 };
  };

  const validateResponse = (response1, response2, result1, result2) => {
    let jsonDataWorkspace = [];
    let isSuccess = false;
    if (result1 === "success" && result2 === "success") {
      isSuccess = true;
      const data1 = response1?.data;
      const data2 = response2?.data;
      jsonDataWorkspace = [...data1, ...data2];
    }
    return { isSuccess: isSuccess, jsonDataWorkspace };
  };

  /**
   * Processes redirect services for SMEC tiles.
   * 
   * When a user has tiles in their workspace that contain a redirectServiceIdForSMEC property,
   * this function automatically preloads those redirect services to ensure they are available
   * in the user's workspace. This is particularly important for GSOC (Global Sales Operations Center)
   * tiles that need to redirect to different services based on segment selection.
   * 
   * Flow:
   * 1. Scans the user's workspace data for tiles with valid redirectServiceIdForSMEC values
   * 2. If redirect tiles are found, makes concurrent API calls to add them to the workspace
   * 3. Validates that all redirect services were successfully added (HTTP 200-299)
   * 4. If successful, fetches the updated workspace data to include the newly added redirect services
   * 5. Returns the updated workspace data or falls back to original data on failure
   * 
   * Use Case Example:
   * - User has "Account Management Requests (Americas Enterprise only)" tile in workspace
   * - This tile has redirectServiceIdForSMEC = 67 (pointing to "Account Management Requests")
   * - Function automatically adds the redirect service (ID 67) to ensure it's preloaded
   * - When user selects segments, the redirect service is immediately available
   * 
   * @param {Array} jsonDataWorkspace - Array of workspace tile objects from API response
   * @returns {Object} - Returns {isSuccess: boolean, jsonDataWorkspace: Array}
   *                    - isSuccess: true if redirects were processed and workspace updated
   *                    - jsonDataWorkspace: Updated workspace data or original data on failure
   */
  const processRedirectServices = async (jsonDataWorkspace) => {
    // 1) Check if any item in jsonDataWorkspace has valid redirectServiceIdForSMEC
    const itemsWithRedirect = jsonDataWorkspace.filter(
      item => item.redirectServiceIdForSMEC && item.redirectServiceIdForSMEC !== null
    );

    if (itemsWithRedirect.length === 0) {
      // No redirect services found, return original data
      return { isSuccess: false, jsonDataWorkspace };
    }

    // 2) Check which redirect services are not already in the workspace
    const existingServiceIds = new Set(jsonDataWorkspace.map(item => item.serviceID));
    const redirectsToAdd = itemsWithRedirect.filter(
      item => !existingServiceIds.has(item.redirectServiceIdForSMEC)
    );

    // If all redirect services already exist in workspace, no need to make API calls
    if (redirectsToAdd.length === 0) {
      return { isSuccess: false, jsonDataWorkspace };
    }

    try {
      // 3) Call addToWorkspace only for redirectServiceIdForSMEC that don't already exist
      const redirectPromises = redirectsToAdd.map(item =>
        addToWorkspace(item.redirectServiceIdForSMEC, false)
      );

      const responses = await Promise.all(redirectPromises);

      // 4) Check if all responses are successful (status 200-299)
      const allSuccessful = responses.every(response =>
        response && response.status >= 200 && response.status < 300
      );

      if (allSuccessful) {
        // Fetch updated data if all redirects were successful
        const { response1, response2, result1, result2 } = await fetchUserWorkspacesAndDefaultTiles();
        const result = validateResponse(response1, response2, result1, result2);
        return result;
      } else {
        // Some redirects failed, return original data
        return { isSuccess: false, jsonDataWorkspace };
      }
    } catch (error) {
      console.error("Error processing redirect services:", error);
      // Return original data in case of error
      return { isSuccess: false, jsonDataWorkspace };
    }
  };

  const getTiles = async () => {
    try {
      if (context ? context.telemetryClient : "") {
        setIsLoading(true);
        if (
          data !== undefined &&
          data.length > 0 &&
          deleteId !== 204 &&
          params.addServiceId !== 201 &&
          !params.userDataChanged &&
          tileAlreadyInWorkspace
        ) {
          setDataServiceWorkspace(data);
          setResultError("success");
          return;
        }

        const { response1, response2, result1, result2 } = await fetchUserWorkspacesAndDefaultTiles();
        const { isSuccess, jsonDataWorkspace } = validateResponse(response1, response2, result1, result2);
        if (isSuccess) {
          setResultError("success");
          const redirectResult = await processRedirectServices(jsonDataWorkspace);
          const finalWorkspaceData = redirectResult.isSuccess ? redirectResult.jsonDataWorkspace : jsonDataWorkspace;
          setDataServiceWorkspace(finalWorkspaceData);
          updateContextData(finalWorkspaceData);
          setDeleteId(0);
          params.setAddServiceId(0);
        } else if (result2 === "error") {
          setResultError("error");
          setErrorData(response2?.data);
          setDataServiceWorkspace([]);
        } else {
          setResultError("error");
          setErrorData(response1?.data);
          setDataServiceWorkspace([]);
        }
      }
    } catch (error) {
      console.error("An error occurred while fetching data.");
    } finally {
      if (Number.isInteger(userId)) {
        setIsLoading(false);
      }
    }
  };

  function getUnsecuredRecommendedTiles(recommendedTiles: any) {
    const services = sessionStorage.getItem("v2_services");
    if (!services) return recommendedTiles;

    const tiles = JSON.parse(services);
    return recommendedTiles?.filter((recTile) => {
      const matchedTile = tiles.find((tile) => tile.name === recTile.name);
      return matchedTile && matchedTile.isSecuredByAzureADGroup === false;
    });
  }

  const getRecommendedTiles = async () => {
    try {
      if (
        context &&
        params.currentUserData?.isAiUser &&
        params?.currentUserData?.isAiServicesEnabled
          ? context.telemetryClient
          : ""
      ) {
        setIsLoadingRecommended(true);
        const responseRecommended = await getConsumptionAPI(
          `/api/V2_UserWorkSpace/GetAllRecommendedWorkspaces/${userId}`,
          context.authClient
        );
        let result1 = checkResponse(responseRecommended);
        if (result1 === "success") {
          setResultErrorRecommendation("success");
          const filteredRecommendedData = getUnsecuredRecommendedTiles(
            responseRecommended?.data
          );
          setRecommendedTile(filteredRecommendedData);
          reduxDispatch(setRecommendedTiles(filteredRecommendedData));
          setIsLoadingRecommended(false);
        } else {
          setErrorData(responseRecommended?.data);
          setResultErrorRecommendation("error");
        }
      } else {
        reduxDispatch(setRecommendedTiles([]));
      }
    } catch (error) {
      console.error("An error occurred while fetching data.");
    } finally {
      if (Number.isInteger(userId)) {
        setIsLoadingRecommended(false);
      }
    }
  };

  // --- Functions moved from V2_Tile.tsx to resolve circular dependency ---

  // Log the event when user click on the Pin button from the Recommended tiles.
  const logUserPinClick = (id: number) => {
    const filteredService = recommendedTile.filter(
      (srv) => srv.serviceID === id
    );

    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETServiceRequestTypeLogData({
        ServiceName: filteredService[0].name,
        RequestType: "",
        TileName: filteredService[0].tileName,
        IsNonIrisService: filteredService[0].service_IsNonIRISService
          ? "true"
          : "false",
        IrisUtterance: filteredService[0].iriS_Utterance,
        profileSetting: `v2`,
      })
    );

    reduxDispatch(
      SETLogEventData({
        elementName: Clicked_On_Pin,
        elementId: "",
        action: "click",
        message: Recommended_Tile_Pin_Telematry_message,
        profileSetting: `v2`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
  };

  const addToWorkspace = async (tileId, refreshTiles = true) => {
    try {
      refreshTiles && logUserPinClick(tileId);
      const apiUrl = "/api/V2_UserWorkSpace/Add";
      const postData = {
        serviceId: tileId,
      };
      const response = await postConsumptionAPI(
        apiUrl,
        postData,
        context.authClient
      );

      if (response.status >= 200 && response.status < 300 && refreshTiles) {
        fetchTilesData();
      }

      return response; // Return response for Promise.all checking
    } catch (error) {
      console.error("An error occurred while fetching api.", error);
      return { status: 500 }; // Return error status for Promise.all checking
    }
  };

  const isTileVisibleForUser = () => {
    const isNonAmerica = params.finalTileObject.isNonAmerica;
    const isAmerica =
      params.finalTileObject.isAmerica &&
      params.finalTileObject.loadForms === true;
    const isAmericaSMC =
      params.finalTileObject.isAmericaSMC &&
      params.finalTileObject.loadForms === true;
    const isUserIdValid = userId !== -1;
    const isCurrentUserDefined = params.currentUserData !== undefined;

    return (
      (isNonAmerica || isAmerica || isAmericaSMC) &&
      isUserIdValid &&
      isCurrentUserDefined &&
      params.finalTileObject.isLoaded === false
    );
  };

  //get the tile name from localstorage
  const getStoredResult = () => {
    return localStorage.getItem(
      `TileVisibility_${userId}_${params.currentUserData.userArea}_${params.finalTileObject.TileName}`
    );
  };

  const handleTileFoundInCatalog = () => {
    // Store the result in localStorage
    localStorage.setItem(
      `TileVisibility_${userId}_${params.currentUserData.userArea}_${params.finalTileObject.TileName}`,
      params.finalTileObject.TileName
    );
    setIsTileFoundInPreference(true);
  };

  const handleTileNotFoundInCatalog = (isTileSecure) => {
    const errorMessage = params?.finalTileObject?.isAmerica
      ? V2_SERVICE_NOT_IN_PREFERENCE_Americas
      : isTileSecure
      ? (params?.finalTileObject?.LockedTileMessage ?? SERVICE_LOCKED)
      : V2_SERVICE_NOT_IN_PREFERENCE_NonAmericas;
    params.finalTileObject["Error"] = errorMessage;
    params.setProfileLoading(false);
    params.setShowBot(true);
    params.finalTileObject.isLoaded = true;
    params.finalTileObject.loadForms = false;
    getTiles();
  };

  const checkVisibilitywithConfig = async () => {
    const checkVisibility = sessionStorage.getItem("CheckVisibility");
    if (
      params.finalTileObject.isAmerica === true ||
      params.finalTileObject.isAmericaSMC === true
    ) {
      if (checkVisibility === "true") {
        return await checkVisibilityOfTile(
          userId,
          params.finalTileObject.TileName,
          context
        );
      } else {
        return true;
      }
    } else if (params.finalTileObject.isNonAmerica === true) {
      return await checkVisibilityOfTile(
        userId,
        params.finalTileObject.TileName,
        context
      );
    }
  };

  const fetchTileVisibility = useCallback(async () => {
    if (isTileVisibleForUser()) {
      // For PURL flows, skip the blocking visibility check — the auto-pin logic in
      // startTileFlowBasedOnSrc will handle finding and pinning the tile.
      // The visibility API uses exact matching and may reject partial tile names from PURLs.
      const isPurlFlow = !!params.finalTileObject.TileName && window.location.search.length > 1 && !params.purlProcessed.current;
      if (isPurlFlow) {
        // Still store result and set preference if tile was previously validated
        const storedResult = getStoredResult();
        if (storedResult !== null && storedResult === params.finalTileObject.TileName) {
          setTileAlreadyInWorkspace(true);
          handleTileFoundInCatalog();
        } else {
          // For PURL, treat as found — auto-pin handles the rest
          setIsTileFoundInPreference(true);
        }
        return;
      }

      let tileFoundInCatalog = false;
      let isTileSecure = await params.checkTileSecurity(
        params.finalTileObject.TileName
      );
      const storedResult = getStoredResult();
      params.setProfileLoading(true);
      params.setShowBot(false);
      if (
        storedResult !== null &&
        storedResult === params.finalTileObject.TileName
      ) {
        setTileAlreadyInWorkspace(true);
        tileFoundInCatalog = true;
      } else {
        tileFoundInCatalog = await checkVisibilitywithConfig();
      }

      if (tileFoundInCatalog !== true || isTileSecure) {
        handleTileNotFoundInCatalog(isTileSecure);
      } else {
        // Store the result in localStorage
        handleTileFoundInCatalog();
      }
    }
  }, [
    params.finalTileObject.isNonAmerica,
    params.finalTileObject.loadForms,
    params.currentUserData,
  ]);

  const fetchTilesData = () => {
    getTiles();
    getRecommendedTiles();
  };

  const handleNonAmericaOrAmerica = async () => {
    if (!isGetTileExecuted) {
      await Promise.all([getTiles(), getRecommendedTiles()]);
      setGetTileExecuted(true);
    }
  };

  const handleLoadedOrNonAmerica = async () => {
    const { finalTileObject } = params;

    if (
      (finalTileObject.isLoaded === false ||
        finalTileObject.isNonAmerica === true) &&
      finalTileObject.Error === ""
    ) {
      await fetchTileVisibility();

      // G10 fix: For PURL flows, always call getTiles() regardless of stale isTileFoundInPreference state.
      // React defers setIsTileFoundInPreference(true) to next render, so it's still false here on first load.
      const isPurlFlow = !!finalTileObject.TileName;
      if (isTileFoundInPreference || finalTileObject.isNonAmerica || isPurlFlow) {
        // Skip re-fetching if auto-pin is in progress to avoid stale data overwriting fresh data
        if (!params.autoPinInProgress.current) {
          await Promise.all([getTiles(), getRecommendedTiles()]);
        }
      }
    } else {
      const activeTabKey = sessionStorage.getItem("activeTabKey");
      if (activeTabKey === "0" || finalTileObject.Error !== "") {
        getTiles();
        getRecommendedTiles();
      }
    }
  };

  const handleUserDataChange = () => {
    ///// here ai logic
    if (params.recommendationLoaded === true) {
      setIsLoadingRecommended(true);
      getRecommendedTiles();
      params.setRecommendationLoaded(false);
    }
    getTiles();
    setSelectedTileId(null);
    params.setShowErrorModal({
      isErrorModalVisible: false,
      errorMessage: "",
    });
  };

  useEffect(() => {
    ///// here ai logic
    if (params.recommendationLoaded === true) {
      getRecommendedTiles();
      params.setRecommendationLoaded(false);
    }
  }, [
    userId,
    params.reccomendedData,
    params.recommendationLoaded,
    isUserDataChanged,
  ]);

  const fetchData = async () => {
    const { isparamurlAmerica, isparamurlNonAmerica, isAmericaSMC, isparamurlIncident } = checkUrlParams();

    // Detect ampersand (&) in tile names that break URL parsing
    if (isparamurlAmerica || isparamurlNonAmerica || isAmericaSMC || isparamurlIncident) {
      const urlParams = new URLSearchParams(window.location.search);
      const tileName = urlParamGet(urlParams, 'tilename') || '';
      const allKeys = Array.from(urlParams.keys()).map(k => k.toLowerCase());
      const unknownKeys = allKeys.filter(k => !KNOWN_PURL_KEYS.has(k));
      if (unknownKeys.length > 0) {
        params.finalTileObject["Error"] = PURL_ERROR_INVALID_PARAMS;
        params.setProfileLoading(false);
        params.finalTileObject.isLoaded = true;
        params.finalTileObject.loadForms = false;
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
    }

    if (!isparamurlAmerica && !isparamurlNonAmerica && !isAmericaSMC && !isparamurlIncident) {
      if (
        !params.finalTileObject.isNonAmerica ||
        !params.finalTileObject.isAmerica ||
        !params.finalTileObject.isAmericaSMC
      ) {
        await handleNonAmericaOrAmerica();
      }
    } else {
      await handleLoadedOrNonAmerica();
    }

    if (isUserDataChanged || serviceDeleted || params.isCanceledPreset) {
      handleUserDataChange();
    }
  };

  // useEffect for fetching tiles
  useEffect(() => {
    if (params.userId > 0) {
      fetchData();
    }
  }, [
    params.currentUserData,
    params.userId,
    deleteId,
    params.finalTileObject.isNonAmerica,
    params.finalTileObject.loadForms,
    isTileFoundInPreference,
    params.isCanceledPreset,
    isWorkspaceLoad,
  ]);

  return {
    dataServiceWorkspace, setDataServiceWorkspace,
    dialogTitle, setDialogTitle,
    isDeleteModalVisible, setIsDeleteModalVisible,
    isDropdownModalVisible, setIsDropdownModalVisible,
    showTileNotFoundModal, setShowTileNotFoundModal,
    isErrorModalVisible, setIsErrorModalVisible,
    deleteModalId, setDeleteModalId,
    deleteId, setDeleteId,
    selectedOptions, setSelectedOptions,
    irisAppName,
    context,
    value, setValue,
    isLoading, setIsLoading,
    isLoadingRecommended, setIsLoadingRecommended,
    resultError, setResultError,
    errorData, setErrorData,
    webFormIrisContext, setWebFormIrisContext,
    recommendedTile, setRecommendedTile,
    ResultErrorRecommendation, setResultErrorRecommendation,
    isWebFormPanelOpen, setIsWebFormPanelOpen,
    selectedTileId, setSelectedTileId,
    selectedServiceTile, setSelectedServiceTile,
    isTileFoundInPreference, setIsTileFoundInPreference,
    tileAlreadyInWorkspace, setTileAlreadyInWorkspace,
    serviceDeleted, setServiceDeleted,
    webFormNames, setWebFormNames,
    isGetTileExecuted, setGetTileExecuted,
    isWorkspaceLoad, setWorkspaceLoad,
    WEBFORMNAMES_LOCAL_STORAGE_KEY,
    isUserDataChanged,
    updateContextData,
    reduxDispatch,
    fetchUserWorkspacesAndDefaultTiles,
    validateResponse,
    processRedirectServices,
    getTiles,
    getUnsecuredRecommendedTiles,
    getRecommendedTiles,
    fetchTilesData,
    checkUrlParams,
    handleNonAmericaOrAmerica,
    handleLoadedOrNonAmerica,
    handleUserDataChange,
    fetchData,
    addToWorkspace,
    fetchTileVisibility,
    logUserPinClick,
    isTileVisibleForUser,
    getStoredResult,
    handleTileFoundInCatalog,
    handleTileNotFoundInCatalog,
  };
};
